import React, { useMemo, useRef, useState } from 'react'
import * as THREE from 'three'
import { useFrame } from '@react-three/fiber'

const BRAND_COLORS = {
  darkest: '#01303f',
  dark: '#02577a',
  medium: '#02a9f7',
  light: '#89d6fb',
  lightest: '#d4f0fc',
}

interface TechnicalCubeProps {
  position?: [number, number, number]
  size?: number
  color?: string
  active?: boolean
}

const TechnicalCube: React.FC<TechnicalCubeProps> = ({
  position = [0, 0, 0],
  size = 1,
  color = BRAND_COLORS.medium,
  active = false,
}) => {
  const [hovered, setHovered] = useState(false)
  const pulseRef = useRef(0)
  
  const geometry = useMemo(() => new THREE.BoxGeometry(size, size, size), [size])
  
  const edgesGeometry = useMemo(() => {
    const edges = new THREE.EdgesGeometry(geometry)
    const posAttr = edges.getAttribute('position')
    const positions = posAttr.array as Float32Array
    const lineDistances: number[] = []
    
    for (let i = 0; i < positions.length; i += 6) {
      const x1 = positions[i]
      const y1 = positions[i + 1]
      const z1 = positions[i + 2]
      const x2 = positions[i + 3]
      const y2 = positions[i + 4]
      const z2 = positions[i + 5]
      const segmentLength = Math.sqrt(
        (x2 - x1) ** 2 + (y2 - y1) ** 2 + (z2 - z1) ** 2
      )
      lineDistances.push(0, segmentLength)
    }
    
    edges.setAttribute(
      'lineDistance',
      new THREE.Float32BufferAttribute(lineDistances, 1)
    )
    return edges
  }, [geometry])
  
  useFrame((_, delta) => {
    if (active) {
      pulseRef.current = (pulseRef.current + delta) % (Math.PI * 2)
    }
  })
  
  const pulseIntensity = active ? (Math.sin(pulseRef.current * 3) + 1) / 2 : 0
  const displayColor = hovered ? BRAND_COLORS.light : color
  const glowIntensity = active ? 0.3 + pulseIntensity * 0.7 : (hovered ? 0.2 : 0)
  
  return (
    <group position={position}>
      <mesh 
        geometry={geometry}
        onPointerOver={() => setHovered(true)}
        onPointerOut={() => setHovered(false)}
      >
        <meshBasicMaterial 
          color={displayColor}
          transparent={true}
          opacity={0.7}
        />
      </mesh>
      
      <lineSegments geometry={edgesGeometry}>
        <lineDashedMaterial
          color="#ffffff"
          dashSize={0.1}
          gapSize={0.05}
          linewidth={1}
          depthTest={true}
          depthFunc={THREE.GreaterDepth}
        />
      </lineSegments>
      
      <lineSegments geometry={edgesGeometry}>
        <lineBasicMaterial 
          color="#ffffff"
          linewidth={1}
        />
      </lineSegments>
      
      {(active || hovered) && (
        <pointLight
          color={active ? BRAND_COLORS.light : BRAND_COLORS.medium}
          intensity={glowIntensity}
          distance={2}
        />
      )}
      

    </group>
  )
}

interface TechnicalConnectionProps {
  start: THREE.Vector3
  end: THREE.Vector3
  color?: string
  active?: boolean
  thickness?: number
}

const TechnicalConnection: React.FC<TechnicalConnectionProps> = ({
  start,
  end,
  color = BRAND_COLORS.light,
  active = false,
  thickness = 0.03,
}) => {
  const pulseRef = useRef(0)
  const packetRef = useRef<THREE.Mesh>(null)
  
  const lineGeometry = useMemo(() => {
    const curve = new THREE.LineCurve3(start, end)
    const geometry = new THREE.TubeGeometry(curve, 20, thickness, 8, false)
    return geometry
  }, [start, end, thickness])
  
  const packetGeometry = useMemo(() => {
    return new THREE.BoxGeometry(thickness * 3, thickness * 3, thickness * 3)
  }, [thickness])
  
  useFrame((_, delta) => {
    if (active && packetRef.current) {
      pulseRef.current = (pulseRef.current + delta * 0.5) % 1
      
      const point = new THREE.Vector3().lerpVectors(start, end, pulseRef.current)
      packetRef.current.position.copy(point)
      
      packetRef.current.rotation.x += delta * 2
      packetRef.current.rotation.y += delta * 2
    }
  })
  
  return (
    <group>
      <mesh geometry={lineGeometry}>
        <meshBasicMaterial 
          color={color}
          transparent={true}
          opacity={active ? 0.6 : 0.3}
        />
      </mesh>
      
      <mesh geometry={lineGeometry}>
        <meshBasicMaterial 
          color="#ffffff"
          wireframe={true}
          transparent={true}
          opacity={0.15}
        />
      </mesh>
      
      {active && (
        <mesh ref={packetRef} geometry={packetGeometry}>
          <meshBasicMaterial 
            color={color}
            transparent={true}
            opacity={0.8}
          />
          <pointLight 
            color={color}
            intensity={0.5}
            distance={1}
          />
        </mesh>
      )}
    </group>
  )
}

interface DataCubeProps {
  position: [number, number, number]
  size?: number
  color?: string
  active?: boolean
}

const DataCube: React.FC<DataCubeProps> = ({
  position,
  size = 0.15,
  color = BRAND_COLORS.light,
  active = false,
}) => {
  const geometry = useMemo(() => new THREE.BoxGeometry(size, size, size), [size])
  
  const edgesGeometry = useMemo(() => new THREE.EdgesGeometry(geometry), [geometry])
  
  return (
    <group position={position}>
      <mesh geometry={geometry}>
        <meshBasicMaterial 
          color={color}
          transparent={true}
          opacity={active ? 0.8 : 0.4}
        />
      </mesh>
      
      <lineSegments geometry={edgesGeometry}>
        <lineBasicMaterial 
          color="#ffffff"
          transparent={true}
          opacity={0.7}
        />
      </lineSegments>
      
      {active && (
        <pointLight 
          color={color}
          intensity={0.3}
          distance={0.5}
        />
      )}
    </group>
  )
}

const connections = [
  { from: 0, to: 1 },
  { from: 1, to: 2 },
  { from: 2, to: 3 },
  { from: 3, to: 4 },
  { from: 1, to: "b1" },
  { from: 2, to: "b2" },
  { from: 3, to: "b3" },
  { from: 1, to: "t1" },
  { from: 2, to: "t2" },
  { from: 3, to: "t3" },
  { from: "t1", to: "t2" },
  { from: "t2", to: "t3" },
  { from: "b1", to: "b2" },
  { from: "b2", to: "b3" }
]

const stages = [
  {
    id: 0,
    label: "Input",
    position: new THREE.Vector3(-5, 0, 0),
    color: BRAND_COLORS.dark,
  },
  {
    id: 1,
    label: "Analyze",
    position: new THREE.Vector3(-2.5, 0, 0),
    color: BRAND_COLORS.medium,
  },
  {
    id: 2,
    label: "Process",
    position: new THREE.Vector3(0, 0, 0),
    color: BRAND_COLORS.medium,
  },
  {
    id: 3,
    label: "Reason",
    position: new THREE.Vector3(2.5, 0, 0),
    color: BRAND_COLORS.medium,
  },
  {
    id: 4,
    label: "Output",
    position: new THREE.Vector3(5, 0, 0),
    color: BRAND_COLORS.light,
  }
]

const branches = [
  {
    id: "b1",
    label: "Context",
    position: new THREE.Vector3(-2.5, -2, 1),
    color: BRAND_COLORS.dark,
    parent: 1
  },
  {
    id: "b2",
    label: "Parse",
    position: new THREE.Vector3(0, -2, 1),
    color: BRAND_COLORS.dark,
    parent: 2
  },
  {
    id: "b3",
    label: "Validate",
    position: new THREE.Vector3(2.5, -2, 1),
    color: BRAND_COLORS.dark,
    parent: 3
  },
  {
    id: "t1",
    label: "Collect",
    position: new THREE.Vector3(-2.5, 2, -1),
    color: BRAND_COLORS.dark,
    parent: 1
  },
  {
    id: "t2",
    label: "Transform",
    position: new THREE.Vector3(0, 2, -1),
    color: BRAND_COLORS.dark,
    parent: 2
  },
  {
    id: "t3",
    label: "Synthesize",
    position: new THREE.Vector3(2.5, 2, -1),
    color: BRAND_COLORS.dark,
    parent: 3
  }
]

const ChainOfThoughtAnimation: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0)
  const clockRef = useRef(0)
  

  
  const dataCubes = useMemo(() => {
    const cubes = []
    
    for (let i = 0; i < 30; i++) {
      const stageIndex = Math.floor(i / 6)
      if (stageIndex >= stages.length) continue
      
      const basePos = stages[stageIndex].position
      const offsetX = (Math.random() - 0.5) * 1.2
      const offsetY = (Math.random() - 0.5) * 1.2
      const offsetZ = (Math.random() - 0.5) * 1.2
      
      const colorOptions = [
        BRAND_COLORS.darkest,
        BRAND_COLORS.dark,
        BRAND_COLORS.medium,
        BRAND_COLORS.light,
        BRAND_COLORS.lightest
      ]
      
      cubes.push({
        id: `cube-${i}`,
        position: [
          basePos.x + offsetX,
          basePos.y + offsetY,
          basePos.z + offsetZ
        ] as [number, number, number],
        stage: stageIndex,
        color: colorOptions[Math.floor(Math.random() * colorOptions.length)]
      })
    }
    
    return cubes
  }, [])
  
  useFrame((_, delta) => {
    clockRef.current += delta
    
    if (clockRef.current > 2) {
      clockRef.current = 0
      setCurrentStep((prev) => (prev + 1) % stages.length)
    }
  })
  
  const getNodeById = (id: number | string) => {
    if (typeof id === 'number') {
      return stages.find(stage => stage.id === id)
    }
    return branches.find(branch => branch.id === id)
  }
  
  const getNodePosition = (id: number | string) => {
    const node = getNodeById(id)
    return node ? node.position : new THREE.Vector3(0, 0, 0)
  }
  
  return (
    <group rotation={[0, 0, 0]}>
      {stages.map(stage => (
        <TechnicalCube
          key={`stage-${stage.id}`}
          position={[stage.position.x, stage.position.y, stage.position.z]}
          size={0.8}
          color={stage.color}
          active={currentStep === stage.id}
        />
      ))}
      
      {branches.map(branch => (
        <TechnicalCube
          key={`branch-${branch.id}`}
          position={[branch.position.x, branch.position.y, branch.position.z]}
          size={0.5}
          color={branch.color}
          active={currentStep === branch.parent}
        />
      ))}
      
      {connections.map((conn, idx) => {
        const fromPos = getNodePosition(conn.from)
        const toPos = getNodePosition(conn.to)
        
        const fromNode = getNodeById(conn.from)
        const toNode = getNodeById(conn.to)
        const isFromMainNode = typeof fromNode?.id === 'number'
        const isToMainNode = typeof toNode?.id === 'number'
        
        const isActive = 
          (isFromMainNode && fromNode.id === currentStep) ||
          (isToMainNode && toNode.id === currentStep) ||
          (!isFromMainNode && branches.find(b => b.id === conn.from)?.parent === currentStep) ||
          (!isToMainNode && branches.find(b => b.id === conn.to)?.parent === currentStep)
        
        return (
          <TechnicalConnection
            key={`conn-${idx}`}
            start={fromPos}
            end={toPos}
            color={BRAND_COLORS.light}
            active={isActive}
          />
        )
      })}
      
      {dataCubes.map(cube => (
        <DataCube
          key={cube.id}
          position={cube.position}
          color={cube.color}
          active={cube.stage === currentStep}
        />
      ))}
      
      <ambientLight intensity={0.4} />
      <directionalLight position={[10, 10, 10]} intensity={0.5} />
    </group>
  )
}

export default ChainOfThoughtAnimation